import { useMemo, useState } from 'react'
import { Alert, Box, Button, Grid, Paper, Stack } from '@mui/material'
import { ConfirmationNumber, Explore, People, Settings } from '@mui/icons-material'
import { DayRange } from '@api/types/dashboard'
import { PageShell } from '@components/common/PageShell'
import { ResponsiveTabs, type ResponsiveTabItem } from '@components/common/ResponsiveTabs'
import { useDashboardStatistics } from '../hooks/useDashboardStatistics'
import { useIsAdmin } from '../components/users/useIsAdmin'
import {
  DaysRangeSelector,
  StatsSummaryCards,
  UserActivityChart,
  CompletionsChart,
  CategoryBreakdownChart,
  RecentUsersTable,
  RecentAdventuresTable,
  RecentTicketsTable,
  SystemHealthPanel,
  QuickActionsPanel,
  NeedsAttention,
} from '../components/dashboard'

/**
 * Tabs are keyed by string rather than index so `?tab=` survives a tab being added, removed or
 * role-gated — <ResponsiveTabs> owns that sync. System reads the performance endpoints, which
 * are Admin-only, so a moderator does not get a tab that can only fail.
 */
const BASE_TABS: ResponsiveTabItem[] = [
  { key: 'users', label: 'Users', icon: <People /> },
  { key: 'adventures', label: 'Adventures', icon: <Explore /> },
  { key: 'tickets', label: 'Tickets', icon: <ConfirmationNumber /> },
]
const SYSTEM_TAB: ResponsiveTabItem = { key: 'system', label: 'System', icon: <Settings /> }

export default function AdminDashboard() {
  const isAdmin = useIsAdmin()
  const [activeTab, setActiveTab] = useState('users')
  const [daysRange, setDaysRange] = useState<DayRange>(30)

  // Memoised: ResponsiveTabs re-runs its URL sync whenever the array identity changes.
  const tabs = useMemo(() => (isAdmin ? [...BASE_TABS, SYSTEM_TAB] : BASE_TABS), [isAdmin])

  const { data: dashboardStats, isLoading, error, refetch } = useDashboardStatistics(daysRange)

  return (
    <PageShell
      title="Dashboard"
      actions={<DaysRangeSelector value={daysRange} onChange={setDaysRange} />}
    >
      <Stack spacing={{ xs: 2, md: 3 }}>
        {error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Failed to load dashboard statistics.
          </Alert>
        )}

        <StatsSummaryCards
          totalUsers={dashboardStats?.totalUsers}
          totalAdventures={dashboardStats?.totalAdventures}
          totalGroups={dashboardStats?.totalGroups}
          isLoading={isLoading}
        />

        <NeedsAttention />

        {/* The lists come before the charts on a phone: three charts are most of a screen of
            scrolling, and the lists are what a moderator opens the dashboard to act on. On a
            desktop the charts sit above, as before. `order` keeps one DOM for both. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
          <Box sx={{ order: { xs: 0, md: 1 } }}>
            <Paper sx={{ mb: 2 }}>
              <ResponsiveTabs tabs={tabs} value={activeTab} onChange={setActiveTab} />
            </Paper>

            {activeTab === 'users' && <RecentUsersTable />}
            {activeTab === 'adventures' && <RecentAdventuresTable />}
            {activeTab === 'tickets' && <RecentTicketsTable />}
            {activeTab === 'system' && isAdmin && (
              <Stack spacing={2}>
                <SystemHealthPanel />
                <QuickActionsPanel />
              </Stack>
            )}
          </Box>

          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ order: { xs: 1, md: 0 } }}>
            <Grid item xs={12} md={6}>
              <UserActivityChart data={dashboardStats?.dailyStats} isLoading={isLoading} />
            </Grid>
            <Grid item xs={12} md={6}>
              <CompletionsChart data={dashboardStats?.dailyStats} isLoading={isLoading} />
            </Grid>
            <Grid item xs={12}>
              <CategoryBreakdownChart data={dashboardStats?.dailyStats} isLoading={isLoading} />
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </PageShell>
  )
}
